"use client";

import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { iLedger } from "@sdk/ledgers/ledgers-models";
import { useQueryWithStore } from "@/store/store.hooks";
import {
  selectAllLedgersQuery,
  selectLedgerById,
} from "@/store/modules/ledgers/ledgers.selectors";
import { LoadAllLedgers } from "@/store/modules/ledgers/ledgers.helpers";
import { selectOrganization } from "@/store/modules/workspace/workspace.selectors";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Landmark } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  groupLedgersByCurrencyAndOwnership,
  formatLedgerOptionLabel,
  isCustomLedger,
  isPaymentAccountSelectableLedger,
  isTradeExpenseSelectableLedger,
  resolveWorkspacePaymentAccountLedgerIds,
  ledgerCommandSearchValue,
  LEDGER_OWNERSHIP_GROUP_LABEL,
  sortLedgersByName,
} from "./ledger-grouping";

const NONE_VALUE = "__none__";

export interface LedgerSelectorProps {
  value: string;
  onValueChange: (ledgerId: string) => void;
  allowNone?: boolean;
  noneLabel?: string;
  placeholder?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  hideLabel?: boolean;
  /** When true (default), archived ledgers are excluded except the current value. */
  activeOnly?: boolean;
  /** When true (default), ledgers are grouped by currency and ownership type. */
  grouped?: boolean;
  /** When true (default), shows a search field in the dropdown. */
  searchable?: boolean;
  /** When true, only custom (non auto-provisioned) ledgers are selectable. */
  customLedgersOnly?: boolean;
  /** When true, custom ledgers and workspace Main Bank Account / Cash are selectable. */
  paymentAccountLedgersOnly?: boolean;
  /** When true, custom ledgers and workspace default ledgers are selectable (trade expenses). */
  tradeExpenseLedgersOnly?: boolean;
  /** When set, only ledgers in this ISO currency are selectable. */
  currencyFilter?: string;
}

export function LedgerSelector({
  value,
  onValueChange,
  allowNone = false,
  noneLabel = "None",
  placeholder = "Select ledger account",
  label = "Ledger",
  id,
  disabled,
  className,
  triggerClassName,
  hideLabel = false,
  activeOnly = true,
  grouped = true,
  searchable = true,
  customLedgersOnly = false,
  paymentAccountLedgersOnly = false,
  tradeExpenseLedgersOnly = false,
  currencyFilter,
}: LedgerSelectorProps) {
  const [open, setOpen] = useState(false);
  const normalizedCurrencyFilter = currencyFilter?.trim().toUpperCase() ?? "";
  const organization = useSelector(selectOrganization);
  const workspacePaymentAccountLedgerIds = useMemo(

    () =>
      paymentAccountLedgersOnly
        ? resolveWorkspacePaymentAccountLedgerIds(
            organization?.data?.defaultLedgerAccounts
          )
        : undefined,
    [paymentAccountLedgersOnly, organization?.data?.defaultLedgerAccounts]
  );


  const {
    state: ledgersQueryState,
    isLoading,
    hasError,
    retry,
  } = useQueryWithStore(selectAllLedgersQuery, LoadAllLedgers, []);

  const selectedLedger = useSelector(selectLedgerById(value ?? ""));

  const ledgers = useMemo(() => {
    const all = ((ledgersQueryState?.list ?? []) as iLedger[]).slice();
    let filtered = activeOnly
      ? all.filter((ledger) => ledger.archived !== true)
      : all;

    if (tradeExpenseLedgersOnly) {
      filtered = filtered.filter((ledger) => isTradeExpenseSelectableLedger(ledger));
    } else if (paymentAccountLedgersOnly) {
      filtered = filtered.filter((ledger) =>
        isPaymentAccountSelectableLedger(
          ledger,
          workspacePaymentAccountLedgerIds
        )
      );
    } else if (customLedgersOnly) {
      filtered = filtered.filter((ledger) => isCustomLedger(ledger));
    }

    if (normalizedCurrencyFilter) {
      filtered = filtered.filter(
        (ledger) =>
          ledger.currency?.trim().toUpperCase() === normalizedCurrencyFilter
      );
    }

    if (activeOnly && value) {
      const selected = all.find((ledger) => ledger.id === value) ?? selectedLedger;
      if (
        selected &&
        !filtered.some((ledger) => ledger.id === selected.id) &&
        (!tradeExpenseLedgersOnly ||
          isTradeExpenseSelectableLedger(selected)) &&
        (!paymentAccountLedgersOnly ||
          isPaymentAccountSelectableLedger(
            selected,
            workspacePaymentAccountLedgerIds
          )) &&
        (!customLedgersOnly || isCustomLedger(selected)) &&
        (!normalizedCurrencyFilter ||
          selected.currency?.trim().toUpperCase() === normalizedCurrencyFilter)
      ) {
        filtered = [...filtered, selected];
      }
    }

    if (!grouped) {
      return sortLedgersByName(filtered);
    }

    return filtered;
  }, [
    ledgersQueryState?.list,
    activeOnly,
    value,
    selectedLedger,
    grouped,
    customLedgersOnly,
    paymentAccountLedgersOnly,
    tradeExpenseLedgersOnly,
    normalizedCurrencyFilter,
    workspacePaymentAccountLedgerIds,
  ]);

  const groupedLedgers = useMemo(
    () => (grouped ? groupLedgersByCurrencyAndOwnership(ledgers) : []),
    [grouped, ledgers]
  );

  const displayLedger =
    selectedLedger ?? ledgers.find((ledger) => ledger.id === value);

  return (
    <div className={cn("grid min-w-0 gap-2", className)}>
      {!hideLabel && label ? <Label htmlFor={id}>{label}</Label> : null}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-10 w-full min-w-0 justify-between overflow-hidden font-normal",
              triggerClassName
            )}
            disabled={disabled || isLoading}
            title={
              value && displayLedger
                ? formatLedgerOptionLabel(displayLedger)
                : undefined
            }
          >
            {isLoading ? (
              <span className="truncate">Loading…</span>
            ) : hasError ? (
              <span className="truncate text-destructive">Error loading ledgers</span>
            ) : value && displayLedger ? (
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {formatLedgerOptionLabel(displayLedger)}
                </span>
              </div>
            ) : value ? (
              <span className="min-w-0 flex-1 truncate text-left">{value}</span>
            ) : (
              <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">
                {placeholder}
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          {hasError ? (
            <div className="p-3 text-sm">
              <p className="mb-2 text-destructive">Failed to load ledgers.</p>
              <Button variant="outline" size="sm" onClick={() => retry()}>
                Retry
              </Button>
            </div>
          ) : (
            <Command className="max-h-[min(320px,var(--radix-popover-content-available-height))]">
              {searchable ? (
                <CommandInput placeholder="Search ledgers..." />
              ) : null}
              <CommandList className="max-h-[min(280px,var(--radix-popover-content-available-height))] overscroll-contain">
                <CommandEmpty>No ledger found.</CommandEmpty>
                {allowNone ? (
                  <CommandGroup>
                    <CommandItem
                      value={NONE_VALUE}
                      onSelect={() => {
                        onValueChange("");
                        setOpen(false);
                      }}
                    >
                      <span className="text-muted-foreground">{noneLabel}</span>
                      <Check
                        className={cn(
                          "ml-auto h-4 w-4",
                          !value ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  </CommandGroup>
                ) : null}
                {grouped
                  ? groupedLedgers.map(({ currency, groups }) =>
                      groups.map(({ ownershipGroup, ledgers: groupLedgers }) => (
                        <CommandGroup
                          key={`${currency}-${ownershipGroup}`}
                          heading={`${currency} · ${LEDGER_OWNERSHIP_GROUP_LABEL[ownershipGroup]}`}
                        >
                          {groupLedgers.map((ledger) =>
                            ledger.id ? (
                              <CommandItem
                                key={ledger.id}
                                value={ledgerCommandSearchValue(ledger)}
                                className="gap-2"
                                onSelect={() => {
                                  onValueChange(ledger.id!);
                                  setOpen(false);
                                }}
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                                  <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span
                                    className="truncate"
                                    title={formatLedgerOptionLabel(ledger)}
                                  >
                                    {formatLedgerOptionLabel(ledger)}
                                  </span>
                                </div>
                                <Check
                                  className={cn(
                                    "ml-auto h-4 w-4 shrink-0",
                                    value === ledger.id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ) : null
                          )}
                        </CommandGroup>
                      ))
                    )
                  : (
                    <CommandGroup>
                      {(ledgers as iLedger[]).map((ledger) =>
                        ledger.id ? (
                          <CommandItem
                            key={ledger.id}
                            value={ledgerCommandSearchValue(ledger)}
                            className="gap-2"
                            onSelect={() => {
                              onValueChange(ledger.id!);
                              setOpen(false);
                            }}
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                              <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span
                                className="truncate"
                                title={formatLedgerOptionLabel(ledger)}
                              >
                                {formatLedgerOptionLabel(ledger)}
                              </span>
                            </div>
                            <Check
                              className={cn(
                                "ml-auto h-4 w-4 shrink-0",
                                value === ledger.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ) : null
                      )}
                    </CommandGroup>
                  )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
